import { defineStore } from 'pinia'
import { store } from '../index'
import { UserLoginType, UserType } from '@/api/login/types'
import { getSystemConfigApi } from '@/api/system/config'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import { clearPreviewTenant } from '@/utils/previewTenant'

interface UserState {
  userInfo?: UserType
  rememberMe: boolean
  loginInfo?: UserLoginType
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      // 记住我
      rememberMe: true,
      loginInfo: undefined
    }
  },
  getters: {
    getToken(): string {
      return this.userInfo?.token || ''
    },
    getPermissions(): Record<string, string[]> {
      return this.userInfo?.permissions || {}
    },
    // 是否管理员：管理员放行所有菜单/权限（与 App 端 Permission.can 的 isAdmin 短路一致）
    getIsAdmin(): boolean {
      return this.userInfo?.is_admin || false
    },
    getUserInfo(): UserType | undefined {
      return this.userInfo
    },
    getConfig(): any {
      return this.userInfo?.config
    },
    getRememberMe(): boolean {
      return this.rememberMe
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },
    getDataAuth(): string {
      return this.userInfo?.data_auth || ''
    },
    getCurrentStore(): number {
      return this.userInfo?.current_store || 0
    },
    getStoreOptions(): any[] {
      return this.userInfo?.store_options || []
    },
    getTableColumn(): Record<string, any[]> {
      return this.userInfo?.table_columns || {}
    }
  },
  actions: {
    setUserInfo(userInfo?: UserType) {
      this.userInfo = userInfo
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          this.reset()
        })
        .catch(() => {})
    },
    reset() {
      // 主动退出与被动登出（401/token 失效）都会走这里。
      // 用动态导入：tracker 也依赖本 store，静态导入会形成循环依赖。
      const path = router.currentRoute.value.path
      import('@/utils/tracker').then(({ sendEvent }) => sendEvent('logout', { path }))
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setUserInfo(undefined)
      clearPreviewTenant()
      router.replace('/login')
    },
    logout() {
      this.reset()
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    },
    setPermissions(permissions: Record<string, string[]>) {
      if (this.userInfo) {
        this.userInfo.permissions = permissions
      }
    },
    setHeadImg(headImg: string) {
      if (this.userInfo) {
        this.userInfo.head_img = headImg
      }
    },
    setStoreName(storeName: string) {
      if (this.userInfo) {
        this.userInfo.store_name = storeName
      }
    },
    setUserName(userName: string) {
      if (this.userInfo) {
        this.userInfo.username = userName
      }
    },
    setExpire(expire: string) {
      if (this.userInfo) {
        this.userInfo.expire = expire
      }
    },
    setConfig(config: any) {
      if (this.userInfo) {
        this.userInfo.config = config
      }
    },
    async refreshConfig() {
      const res = await getSystemConfigApi().catch(() => null)
      if (res?.data) {
        this.setConfig({ ...(this.getConfig || {}), ...res.data })
      }
    },
    setDataAuth(dataAuth: string) {
      if (this.userInfo) {
        this.userInfo.data_auth = dataAuth
      }
    },
    setCurrentStore(currentStore: number) {
      if (this.userInfo) {
        this.userInfo.current_store = currentStore
      }
    },
    setStoreOptions(storeOptions: any[]) {
      if (this.userInfo) {
        this.userInfo.store_options = storeOptions
      }
    },
    setTableColumns(table_columns: Record<string, any[]>) {
      if (this.userInfo) {
        this.userInfo.table_columns = table_columns
      }
    },
    setTableColumnByTableName(tableName: string, columns: any[]) {
      // 只保存必要的字段
      const settings = columns.map((col) => ({
        field: col.field,
        hidden: col.hidden || false,
        label: col.label || '',
        fixed: col.fixed
      }))
      if (this.userInfo) {
        if (!this.userInfo.table_columns) {
          this.userInfo.table_columns = {}
        }
        this.userInfo.table_columns[tableName] = settings
      }
    },
    getTableColumnByTableName(tableName: string): any[] {
      if (!this.userInfo?.table_columns) {
        return []
      }
      return this.userInfo.table_columns[tableName] || []
    }
  },
  persist: true
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
